/**
 * GL context initialization with iOS-friendly defaults:
 *  - DPR clamped to MAX_DPR (2.0 by default, even on iPhone ProMotion 3.0)
 *  - antialias off (we do SDF AA in shader)
 *  - premultiplied alpha on, blending src=ONE dst=ONE_MINUS_SRC_ALPHA
 */

export const MAX_DPR = 2.0;
export const DEFAULT_RENDER_SCALE = 0.75; // internal resolution scale (Sprint 4 tunes)

export interface GLContext {
  gl: WebGL2RenderingContext;
  canvas: HTMLCanvasElement;
  dpr: number;
  /** Current render scale, may be dynamically scaled down. */
  renderScale: number;
  /** Render scale we want to be at (default 0.75). */
  targetRenderScale: number;
}

export function initGL(canvas: HTMLCanvasElement): GLContext | null {
  const gl = canvas.getContext("webgl2", {
    alpha: true,
    antialias: false,
    premultipliedAlpha: true,
    depth: false,
    stencil: false,
    powerPreference: "high-performance",
    preserveDrawingBuffer: false,
  });
  if (!gl) {
    console.error("[nspp] WebGL2 not available");
    return null;
  }

  const dpr = Math.min(MAX_DPR, window.devicePixelRatio || 1);
  const renderScale = DEFAULT_RENDER_SCALE;
  resizeCanvas(canvas, gl, dpr * renderScale);

  // global state — only set once
  gl.disable(gl.DEPTH_TEST);
  gl.disable(gl.CULL_FACE);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

  return { gl, canvas, dpr, renderScale, targetRenderScale: renderScale };
}

export function resizeCanvas(
  canvas: HTMLCanvasElement,
  gl: WebGL2RenderingContext,
  backbufferMul: number,
): void {
  const w = Math.max(1, Math.floor(canvas.clientWidth * backbufferMul));
  const h = Math.max(1, Math.floor(canvas.clientHeight * backbufferMul));
  if (canvas.width !== w || canvas.height !== h) {
    canvas.width = w;
    canvas.height = h;
    gl.viewport(0, 0, w, h);
  }
}

export function compileShader(
  gl: WebGL2RenderingContext,
  type: number,
  src: string,
): WebGLShader | null {
  const sh = gl.createShader(type);
  if (!sh) return null;
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    console.error("[nspp] shader compile failed:\n", gl.getShaderInfoLog(sh), "\n--- src ---\n", src);
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

export function linkProgram(
  gl: WebGL2RenderingContext,
  vsSrc: string,
  fsSrc: string,
  attribs: Record<string, number> = RECT_ATTRIBS,
): WebGLProgram | null {
  const vs = compileShader(gl, gl.VERTEX_SHADER, vsSrc);
  const fs = compileShader(gl, gl.FRAGMENT_SHADER, fsSrc);
  if (!vs || !fs) return null;
  const p = gl.createProgram();
  if (!p) return null;
  gl.attachShader(p, vs);
  gl.attachShader(p, fs);
  for (const [name, loc] of Object.entries(attribs)) {
    gl.bindAttribLocation(p, loc, name);
  }
  gl.linkProgram(p);
  gl.deleteShader(vs);
  gl.deleteShader(fs);
  if (!gl.getProgramParameter(p, gl.LINK_STATUS)) {
    console.error("[nspp] program link failed:", gl.getProgramInfoLog(p));
    gl.deleteProgram(p);
    return null;
  }
  return p;
}
/** Default attribute layout for the rect pipeline.
 *
 *  Per-instance locations 0-14 — 15 vec4 attributes total.
 *   0-9:   posSize, fill, stroke, shadow, shadowColor, xform, prev, misc, halfSize, radii
 *   10-12: filterA, filterB, gradient (Layer 7)
 *   13-14: sdfParamsA, sdfParamsB (SDF library)
 *
 *  SDF kind id is packed into aMisc.w as id/15.0 and decoded in the vertex
 *  shader (vSdfKind = aMisc.w * 15.0). The engine keeps 15 attributes
 *  instead of 16 because WebGL2 caps MAX_VERTEX_ATTRIB_STRIDE at 255 bytes
 *  on most implementations, and 16 × 16 = 256 fails the cap.
 *
 *  No per-vertex attributes: the unit-quad corner is derived in the vertex
 *  shader from gl_VertexID.
 */
export const RECT_ATTRIBS: Record<string, number> = {
  aPosSize: 0,
  aFill: 1,
  aStroke: 2,
  aShadow: 3,
  aShadowColor: 4,
  aXform: 5,
  aPrev: 6,
  aMisc: 7,
  aHalfSize: 8,
  aRadii: 9,
  aFilterA: 10,
  aFilterB: 11,
  aGradient: 12,
  aSdfParamsA: 13,
  aSdfParamsB: 14,
};

/** Attribute layout for the postprocess pipeline. */
export const POST_ATTRIBS: Record<string, number> = {
  aPos: 0,
};
